'use client';

import type { ReactNode } from 'react';
import { AlarmClock, UserRound } from 'lucide-react';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { formatDateAU } from '@/lib/formatters/australian-formatters';
import { HandedOverDetail } from './handed-over-summary';
import {
  isHandoverOverdue,
  type Handling
} from '@/lib/services/matter-handling';

/**
 * "Handled by [Name]" — the one badge for external handling (EH-DI-002).
 *
 * **Why info/subtle and not neutral, given EH-DI-002 says "greyed, not hidden".**
 * Two requirements pull against each other here and the resolution is deliberate:
 *
 * - EH-DI-002 wants this de-emphasised — it is context, not a call to action.
 * - EH-DI-004 forbids it reading like "Notified externally", which is already a
 *   *neutral grey* chip with a *check* icon on the very same Discovery row, and
 *   which means the opposite thing (*the firm did this step outside Vale*, versus
 *   *the firm is not doing this work at all*).
 *
 * A lawyer seeing two identical grey chips learns nothing, so the de-emphasis is
 * carried by `emphasis="subtle"` (a quiet fill, no border) while the *ramp* stays
 * distinct. Do not "tidy" this back to `tone="neutral"`: that is precisely the
 * collision EH-DI-004 exists to prevent, and it looks like a simplification.
 *
 * The person icon and the handler's name do the rest of the work. Neither is
 * decoration. There is deliberately **no short "External" label anywhere**
 * (EH-DI-002): the word already means two other things on a Discovery row.
 *
 * **Overdue (EH-010, VALE-842) is a treatment of this badge, not a second chip.**
 * When the expected-by date has passed the badge moves to the caution ramp, swaps
 * the person icon for a clock and appends "overdue", while keeping "Handled by
 * [Name]" verbatim. That pairing is the whole point: the row resurfaces on the
 * Discovery list it was folded out of, and the retained handler text is what stops
 * it reading as ordinary outstanding firm work. Do not promote this to `critical`
 * or to its own chip. A late handover is something to look at, not a failure, and
 * a second chip on the row costs the height the truncation below exists to save.
 *
 * `handlingOverdue` is computed by the backend; this component never compares the
 * date itself. See `isHandoverOverdue` for why.
 */
export function HandledByBadge({
  handling,
  className,
  trailing,
  tone,
  withTitle = true
}: {
  handling: Handling | null | undefined;
  className?: string;
  /**
   * Drawn inside the pill, after the text: the review's change affordance
   * (VALE-1866), which must read as part of the badge rather than beside it.
   */
  trailing?: ReactNode;
  /**
   * The AI review's change hue for a PROPOSED handler change (VALE-1866):
   * `caution` is the warning ramp the review's UPDATED tag and updated values
   * use, so the whole pill reads as the updated value, with the outgoing
   * handler struck beside it. Overdue still wins: it already draws caution.
   */
  tone?: 'info' | 'caution';
  /**
   * The native `title` carrying the full name. Off where a hover card already
   * shows it (the AI review, VALE-1866): the browser tooltip otherwise paints
   * over that card.
   */
  withTitle?: boolean;
}) {
  if (!handling?.handledBy) return null;

  const { name } = handling.handledBy;
  const { handlingNote, handlingExpectedBy } = handling;
  const hasDetail = !!handlingNote || !!handlingExpectedBy;
  const overdue = isHandoverOverdue(handling);

  /**
   * The accessible title carries the date; the visible chip does NOT (VALE-1854).
   *
   * "Overdue against what?" is a real question — it was asked by the first person
   * to drive this feature without having read the code — but *the chip is the wrong
   * place to answer it*. Rendering `overdue since 10/09/2026` was tried in the
   * session and reverted: on a dense list row the date was itself truncated, to
   * `overdue since 10`, which reads as a broken string rather than a date, and the
   * extra width came straight out of the ROW'S OWN NAME. A badge that costs the
   * reader the name of the asset it is describing has taken more than it gave.
   *
   * So the answer lives in the two places that have room for a sentence: this
   * title, and the tooltip below, whose wording now says whose date it is rather
   * than just what it was. The chip stays three short words.
   */
  const overdueLabel = handlingExpectedBy
    ? `overdue since ${formatDateAU(handlingExpectedBy)}`
    : 'overdue';

  const badge = (
    <StatusBadge
      tone={overdue ? 'caution' : (tone ?? 'info')}
      emphasis="subtle"
      icon={overdue ? AlarmClock : UserRound}
      className={className}
      data-testid="handled-by-badge"
      data-overdue={overdue ? 'true' : undefined}
    >
      {/*
        Truncate rather than wrap. A handler can be an organisation
        ("Perpetual Trustee Company Limited") and an unbounded name wraps the chip
        to two lines, which blows the height of every row it sits in — caught by
        the VALE-841 self-QA render. Same idiom as the discovery row's
        "Secured against …" badge. The full name stays reachable in the tooltip
        and the accessible title.
      */}
      <span
        className="max-w-[22ch] truncate"
        title={
          withTitle
            ? overdue
              ? `Handled by ${name}, ${overdueLabel}`
              : `Handled by ${name}`
            : undefined
        }
      >
        Handled by {name}
      </span>
      {/*
        Outside the truncating span, so a long organisation name eats its own
        width and never the word that says the handover is late.
      */}
      {overdue ? <span className="shrink-0">&middot; overdue</span> : null}
      {trailing}
    </StatusBadge>
  );

  if (!hasDetail) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {/*
          **`HandedOverDetail`, not a second copy of the same two sentences**
          (VALE-1854).

          It says whose date this is — "Expected by 10/09/2026" reads as a
          deadline somebody imposed on the firm, when it is the firm's own
          optional note from the hand-over dialog and nothing external is
          measured against it. It also picks the tense from `handlingOverdue`,
          which is the half a hand-written copy here got wrong: the tooltip said
          "expected" for a handover dated a month out, so a date not yet reached
          read as one already missed.

          Binding to the shared component rather than repeating its ternary is
          what stops the two drifting again — this tooltip and the two panels now
          word a handover the one way.
        */}
        <p className="text-muted-foreground">
          <HandedOverDetail handling={handling} />
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
